import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';

import { ConnectedOverlayScrollHandler, DomHandler } from 'primeng/dom';

import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { DatePickerService } from '../../services/date-picker.service';

/**
 * Floating container used by every date picker variant.
 *
 * Responsibilities:
 *  - moves its panel to <body> so it escapes overflow/stacking contexts
 *  - opens/closes with the height animation
 *  - positions itself relative to `anchor` (below → above → right → left)
 *  - traps Tab inside itself and reports Escape
 *  - closes itself on scroll of the anchor's scroll parents and on window resize
 *
 * Whatever is projected (calendar panel, footer buttons, p-header/p-footer) lives inside the trap.
 */
@Component({
    selector: 'agpr-date-picker-overlay',
    templateUrl: './date-picker-overlay.component.html',
    styleUrls: ['./date-picker-overlay.component.scss'],
    animations: [
        trigger('overlayAnimation', [
            state('hidden', style({ height: '0' })),
            // '*' = measured auto height, so the overlay fits whatever is projected (e.g. a footer)
            state('visible', style({ height: '*' })),
            transition('visible <=> hidden', animate('.12s cubic-bezier(0, 0, 0.2, 1)'))
        ])
    ],
    standalone: false
})
export class DatePickerOverlayComponent implements AfterViewInit, OnDestroy {

    /** Element the overlay is positioned against. */
    @Input() anchor: HTMLElement;
    @Input() panelStyleClass: string;
    /** Gap in px between the anchor and the overlay. */
    @Input() gap: number = 5;

    /** Close when the user presses outside both the overlay and its anchor. */
    @Input() closeOnOutsideClick: boolean = true;

    @Output() opened = new EventEmitter<AnimationEvent>();
    @Output() closed = new EventEmitter<AnimationEvent>();
    @Output() escape = new EventEmitter<KeyboardEvent>();

    @Output() outsideClick = new EventEmitter<MouseEvent>();

    @ViewChild('panel', { static: true }) panelRef: ElementRef<HTMLElement>;

    visible = false;

    private scrollHandler: ConnectedOverlayScrollHandler;

    constructor(
        private cd: ChangeDetectorRef,
        private datePickerService: DatePickerService
    ) {}

    get element(): HTMLElement {
        return this.panelRef.nativeElement;
    }

    ngAfterViewInit() {
        document.body.appendChild(this.element);
    }

    /**
     * Opens the overlay. Hosts should run change detection first so the projected
     * content is up to date — its size is measured to choose a position.
     */
    open(): void {
        if (this.visible) {
            return;
        }
        const el = this.element;
        el.style.zIndex = String(++DomHandler.zindex);
        el.style.display = 'block';
        this.align(this.measure(el));
        this.visible = true;
        this.cd.markForCheck();
    }

    close(): void {
        if (!this.visible) {
            return;
        }
        this.visible = false;
        this.cd.markForCheck();
    }

    onKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case ActionKeys.TAB:
                this.datePickerService.trapFocus(event, this.element);
                break;
            case ActionKeys.ESCAPE:
                this.escape.emit(event);
                break;
        }
    }

    onAnimationStart(event: AnimationEvent): void {
        if (event.toState === 'visible') {
            this.addListeners();
        }
    }

    onAnimationDone(event: AnimationEvent): void {
        // skip the initial void -> hidden callback
        if (event.fromState === 'void') {
            return;
        }
        if (event.toState === 'visible') {
            this.opened.emit(event);
        } else {
            this.element.style.display = 'none';
            this.removeListeners();
            this.closed.emit(event);
        }
    }

    private documentMousedownListener(event: MouseEvent) {
        const target = event.target as HTMLElement;
        // The anchor is excluded so the trigger's own click can still toggle the overlay.
        if (this.element.contains(target) || this.anchor?.contains(target)) {
            return;
        }
        if (this.datePickerService.isFalseClickOutside(target)) {
            return;
        }
        this.outsideClick.emit(event);
        if (this.closeOnOutsideClick) {
            this.close();
        }
    };

    private resizeListener = () => {
        if (!DomHandler.isTouchDevice()) {
            this.close();
        }
    };

    /** Natural size of the content, measured synchronously so nothing paints in between. */
    private measure(el: HTMLElement): { width: number; height: number } {
        const prevHeight = el.style.height;
        el.style.visibility = 'hidden';
        el.style.height = 'auto';

        const size = { width: el.offsetWidth, height: el.offsetHeight };
        el.style.height = prevHeight;
        el.style.visibility = '';
        return size;
    }

    private align({ width, height }: { width: number; height: number }): void {
        if (!this.anchor) {
            return;
        }
        const rect = this.anchor.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        let top: number;
        let left: number;

        // below
        if (viewportHeight - rect.bottom - this.gap > height) {
            top = rect.bottom + this.gap;
            left = rect.left;
        // above
        } else if (rect.top - this.gap > height) {
            top = rect.top - height - this.gap;
            left = rect.left;
        // right, vertically centred
        } else if (viewportWidth - rect.right > width) {
            top = (viewportHeight - height) / 2;
            left = rect.right;
        // left, vertically centred
        } else {
            top = (viewportHeight - height) / 2;
            left = rect.left - width - this.gap;
        }

        // The panel is position: absolute inside <body>, so convert viewport coords to document coords.
        this.element.style.top = top + window.scrollY + 'px';
        this.element.style.left = left + window.scrollX + 'px';
    }

    private addListeners(): void {
        if (!this.scrollHandler && this.anchor) {
            this.scrollHandler = new ConnectedOverlayScrollHandler(this.anchor, () => this.close());
        }
        this.scrollHandler?.bindScrollListener();
        window.addEventListener('resize', this.resizeListener);

        document.addEventListener('mousedown', this.documentMousedownListener.bind(this), true);
    }

    private removeListeners(): void {
        this.scrollHandler?.unbindScrollListener();
        window.removeEventListener('resize', this.resizeListener);

        document.removeEventListener('mousedown', this.documentMousedownListener.bind(this), true);
    }

    ngOnDestroy() {
        this.removeListeners();
        this.element.remove();
    }
}
